import * as rruleNs from 'rrule';
const ns = rruleNs;
const rrule = ns.RRule ? ns : ns['default'];
const { RRule, rrulestr } = rrule;
import { addDays, compareDates, dateToUtc, utcToDate } from './date.js';
const FREQ = { daily: RRule.DAILY, weekly: RRule.WEEKLY, monthly: RRule.MONTHLY, yearly: RRule.YEARLY };
const WD = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU];
export function buildRule(spec) {
    const rule = new RRule({
        freq: FREQ[spec.freq],
        interval: spec.interval && spec.interval > 1 ? spec.interval : undefined,
        byweekday: spec.byWeekday?.length ? spec.byWeekday.map((i) => WD[i]) : undefined,
        bymonthday: spec.byMonthDay?.length ? spec.byMonthDay : undefined,
        until: spec.until ? dateToUtc(spec.until) : undefined,
        count: spec.count ?? undefined,
    });
    // strip the generated DTSTART; the task owns the start date
    return rule.toString().split('\n').filter((l) => l.startsWith('RRULE:')).join('\n') || `RRULE:FREQ=${spec.freq.toUpperCase()}`;
}
export function parseRule(rule, startDate) {
    const text = rule.startsWith('RRULE:') ? rule : `RRULE:${rule}`;
    const parsed = rrulestr(`DTSTART:${startDate.replace(/-/g, '')}T000000Z\n${text}`, { forceset: false });
    return parsed;
}
/** Every date this task falls on between `from` and `to`, inclusive.
 *  A task with no rule yields its start date if it is in range. */
export function expand(startDate, rule, from, to) {
    if (!rule)
        return compareDates(startDate, from) >= 0 && compareDates(startDate, to) <= 0 ? [startDate] : [];
    const r = parseRule(rule, startDate);
    // `to` is inclusive; rrule's between() upper bound is exclusive of the next day
    return r.between(dateToUtc(from), dateToUtc(addDays(to, 1)), true).map(utcToDate).filter((d) => d <= to);
}
/** The next `n` dates on or after `after`. Used for the "Next 3:" preview. */
export function nextOccurrences(startDate, rule, after, n = 3) {
    if (!rule)
        return compareDates(startDate, after) >= 0 ? [startDate] : [];
    const r = parseRule(rule, startDate);
    const out = [];
    let cursor = dateToUtc(after);
    for (let i = 0; i < n; i++) {
        const next = r.after(cursor, true);
        if (!next)
            break;
        out.push(utcToDate(next));
        cursor = new Date(next.getTime() + 86_400_000);
    }
    return out;
}
const WEEKDAY_SET = [0, 1, 2, 3, 4].join();
export function repeatKindOf(rule) {
    if (!rule)
        return 'none';
    let r;
    try {
        r = parseRule(rule, '2000-01-03');
    }
    catch {
        return 'custom';
    }
    const o = r.origOptions;
    // An interval, an end date or a count is detail the five buttons cannot hold.
    if ((o.interval && o.interval > 1) || o.until || o.count)
        return 'custom';
    if (o.bymonthday || o.bysetpos || o.bymonth)
        return 'custom';
    const days = weekdaysOfRule(rule);
    switch (o.freq) {
        case RRule.DAILY: return days.length ? 'custom' : 'daily';
        case RRule.WEEKLY:
            if (!days.length)
                return 'weekly';
            return days.join() === WEEKDAY_SET ? 'weekdays' : 'weekly';
        case RRule.MONTHLY: return days.length ? 'custom' : 'monthly';
        default: return 'custom';
    }
}
/** The inverse: the rule a picked kind means. `weekly` needs the days it falls
 *  on; with none given it repeats on whatever weekday the task starts. */
export function ruleForKind(kind, weekdays = []) {
    switch (kind) {
        case 'daily': return buildRule({ freq: 'daily' });
        case 'weekdays': return buildRule({ freq: 'weekly', byWeekday: [0, 1, 2, 3, 4] });
        case 'weekly': return buildRule({ freq: 'weekly', byWeekday: weekdays.length ? weekdays : undefined });
        case 'monthly': return buildRule({ freq: 'monthly' });
        default: return null;
    }
}
/** The weekdays a rule names, as 0 = Monday … 6 = Sunday. Empty when the rule
 *  does not pin any — used to pre-fill the day picker when a quick-add line is
 *  carried into the detailed form. */
export function weekdaysOfRule(rule) {
    if (!rule)
        return [];
    let r;
    try {
        r = parseRule(rule, '2000-01-03');
    }
    catch {
        return [];
    }
    const bw = r.origOptions.byweekday;
    if (!bw)
        return [];
    const arr = Array.isArray(bw) ? bw : [bw];
    return arr.map((w) => (typeof w === 'number' ? w : w.weekday)).sort((a, b) => a - b);
}
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Plain-English summary. The design system requires the recurrence to be readable
 *  as a sentence, not as an RRULE. */
export function describeRule(rule, opts = {}) {
    if (!rule)
        return '';
    let r;
    try {
        r = parseRule(rule, '2000-01-03');
    }
    catch {
        return 'Repeats';
    }
    const o = r.origOptions;
    const n = o.interval && o.interval > 1 ? o.interval : 1;
    const names = opts.short ? DAY_SHORT : DAY_NAMES;
    const days = (() => {
        const bw = o.byweekday;
        if (!bw)
            return [];
        const arr = Array.isArray(bw) ? bw : [bw];
        return arr.map((w) => (typeof w === 'number' ? w : w.weekday)).sort((a, b) => a - b);
    })();
    const list = (xs) => xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} ${opts.short ? '&' : 'and'} ${xs[xs.length - 1]}`;
    switch (o.freq) {
        case RRule.DAILY:
            return n === 1 ? 'Every day' : `Every ${n} days`;
        case RRule.WEEKLY: {
            if (days.length === 5 && days.every((d, i) => d === i))
                return 'Weekdays';
            if (days.length === 2 && days[0] === 5 && days[1] === 6)
                return 'Weekends';
            const named = list(days.map((d) => names[d]));
            if (n === 1)
                return days.length ? (opts.short ? named : `Every week on ${named}`) : 'Every week';
            return days.length ? `Every ${n} weeks on ${named}` : `Every ${n} weeks`;
        }
        case RRule.MONTHLY:
            return n === 1 ? 'Every month' : `Every ${n} months`;
        case RRule.YEARLY:
            return n === 1 ? 'Every year' : `Every ${n} years`;
        default:
            return 'Repeats';
    }
}
//# sourceMappingURL=recurrence.js.map